import React from "react";
import PageHeader from "@/components/PageHeader";

const expertise = [
  "Capital Formation",
  "Governance",
  "Infrastructure",
  "Agriculture",
  "Hospitality",
  "Healthcare",
  "Government Relations",
];

const approach = [
  {
    no: "01",
    title: "Sponsor-led structuring",
    body: "We originate and structure each platform with the governance and capital architecture required to attract institutional capital.",
  },
  {
    no: "02",
    title: "Aligned stakeholders",
    body: "We work alongside government, communities, development finance institutions and strategic partners — building durable coalitions, not transactional relationships.",
  },
  {
    no: "03",
    title: "Long-horizon stewardship",
    body: "We measure success across decades, not quarters. Platforms are designed for compounding impact and resilient performance through cycles.",
  },
];

export default function About() {
  return (
    <>
      <PageHeader
        testId="about-header"
        eyebrow="About"
        title="A UK-based sponsor of strategic platforms."
        intro="Evergreen Global Holdings Ltd brings together capital, governance and execution to develop platforms of national importance — built to endure, designed to scale."
        image="https://customer-assets.emergentagent.com/job_evergreen-holdings/artifacts/g77p5tw6_IMG_0644.jpeg"
      />

      {/* Overview */}
      <section data-testid="about-overview" className="evg-section bg-white">
        <div className="evg-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-16 gap-y-12">
            <div className="lg:col-span-5" data-reveal>
              <div className="evg-overline mb-8">Overview</div>
              <h2 className="evg-display-lg">Sponsoring, governing and developing strategic platforms.</h2>
            </div>
            <div className="lg:col-span-7" data-reveal data-reveal-delay="200">
              <p className="evg-body">
                Evergreen Global Holdings Ltd is a UK-based holding company focused on sponsoring,
                governing and developing strategic platforms that create long-term economic value.
                We are deliberately positioned as a sponsor and stewardship layer — not an operator
                of a single project — providing the governance, capital architecture and partner
                relationships that allow each platform to scale on its own merits.
              </p>
              <p className="evg-body mt-7">
                The company brings together expertise across the disciplines required to execute
                projects of national importance: from structured finance through to construction,
                operations, regulatory engagement and impact measurement.
              </p>

              <div className="mt-16 pt-10 border-t border-[#0A1F44]/12">
                <div className="evg-overline mb-8">Areas of Expertise</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-4">
                  {expertise.map((e, i) => (
                    <div key={e} className="flex items-baseline gap-5 py-2">
                      <span className="evg-index">{String(i + 1).padStart(2, "0")}</span>
                      <span className="text-[15px] text-[#0A1F44] font-light">{e}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section data-testid="about-vision-mission" className="evg-section bg-[#F5F6F8]">
        <div className="evg-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-20 gap-y-16">
            <div className="border-t border-[#D4AF37] pt-12" data-reveal>
              <div className="evg-overline mb-6">Vision</div>
              <h3 className="evg-display-md">World-class platforms that compound value over decades.</h3>
              <p className="mt-8 evg-body">
                To create world-class strategic platforms that generate sustainable economic
                growth, strengthen communities and deliver attractive long-term investor returns.
              </p>
            </div>
            <div className="border-t border-[#D4AF37] pt-12" data-reveal data-reveal-delay="200">
              <div className="evg-overline mb-6">Mission</div>
              <h3 className="evg-display-md">Identify, structure, execute — with measurable national impact.</h3>
              <p className="mt-8 evg-body">
                To identify, structure and execute high-impact opportunities that combine
                commercial performance with measurable national development outcomes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Approach */}
      <section data-testid="about-approach" className="evg-section bg-white">
        <div className="evg-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-16 gap-y-12">
            <div className="lg:col-span-4" data-reveal>
              <div className="evg-overline mb-8">Our Approach</div>
              <h2 className="evg-display-lg">Discipline. Patience. Partnership.</h2>
            </div>
            <div className="lg:col-span-8">
              {approach.map((item, i) => (
                <div
                  key={item.no}
                  data-reveal
                  data-reveal-delay={String(i * 100)}
                  className="py-12 border-t border-[#0A1F44]/12 last:border-b last:border-[#0A1F44]/12 grid grid-cols-12 gap-x-8 gap-y-4 items-baseline"
                >
                  <span className="col-span-12 md:col-span-1 evg-index">{item.no}</span>
                  <h3 className="col-span-12 md:col-span-4 font-serif text-2xl md:text-3xl leading-tight">{item.title}</h3>
                  <p className="col-span-12 md:col-span-7 text-[15px] text-[#0A1F44]/72 leading-[1.8] font-light">
                    {item.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
